
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import CompetitorLogo from '@/components/sports/common/CompetitorLogo.vue';
import NoOfferNotifier from '@/components/sports/common/event/NoOfferNotifier.vue';

import { ObjectType, SingleStepSportEvent, SportEventStatus, SportOffer } from '@/@types/graphql-types';
import { SportLocale, ToggleMySportsNotificationPayload } from '@/enums/sports';
import { Locale } from '@/helpers/locale-helper';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { BuyboxParam, trackClickOutSports } from '@/helpers/offer-helper';
import { SingleStepContext } from '@/helpers/sports/config';
import { formatDate, formatTime } from '@/helpers/time-zone-helper';
import { SnowplowSportContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import Provider from '@/interfaces/provider';

const Constant = namespace('constant');
const Language = namespace('language');
const User = namespace('user');

@Component({
	name: 'SingleStepEventEncounter',
	components: {
		ProviderIcon,
		CompetitorLogo,
		NoOfferNotifier,
	},
})
export default class SingleStepEventEncounter extends Vue {
	@Prop() domainSessionId: string;
	@Prop() listIndex: number;
	@Prop({ required: true }) event: SingleStepSportEvent;
	@Prop({ required: true }) context: SingleStepContext;

	@Constant.Getter providersById: Record<string, Provider>;

	@Language.Getter country: string;
	@Language.Getter language: string;
	@Language.State locales: Locale[];
	@Language.State webLocale: SportLocale;

	@User.State timeZone: string | undefined;

	get sportContext() {
		return SnowplowSportContext.fromSportEventV2(this.event.sport, ObjectType.SportEvent, this.event);
	}

	get competitors() {
		return this.context.getCompetitors(this.event, this.webLocale);
	}

	get isDoublesEvent() {
		return this.competitors.start.length === 2 && this.competitors.end.length === 2;
	}

	get startName() {
		return this.competitors.start.map(team => team.name).join(' / ');
	}

	get endName() {
		return this.competitors.end.map(team => team.name).join(' / ');
	}

	get start() {
		return this.competitors.start[0];
	}

	get end() {
		return this.competitors.end[0];
	}

	get eventStatus() {
		return this.event.content.status;
	}

	get isLive() {
		return this.eventStatus === SportEventStatus.Live;
	}

	get isEnded() {
		return [SportEventStatus.Ended, SportEventStatus.Closed].includes(this.eventStatus);
	}

	get isCancelled() {
		return this.eventStatus === SportEventStatus.Cancelled;
	}

	get isDelayed() {
		return [SportEventStatus.Postponed, SportEventStatus.Suspended, SportEventStatus.Delayed].includes(
			this.eventStatus
		);
	}
	get isNotNBA() {
		if (this.event.competition?.id === 'scn-bkb-8331232') return false;
		return true;
	}
	get gameStatusContent() {
		if (this.isEnded) return this.$t('SPORTS_WEBAPP_ENDED');
		if (this.isLive) return this.$t('SPORTS_WEBAPP_LIVE');
		if (this.isCancelled) return this.$t('SPORTS_WEBAPP_CANCELLED');
		if (this.eventStatus === SportEventStatus.Postponed) return this.$t('SPORTS_WEBAPP_POSTPONED');
		if (this.eventStatus === SportEventStatus.Delayed) return this.$t('SPORTS_WEBAPP_DELAYED');
		if (this.eventStatus === SportEventStatus.Suspended) return this.$t('SPORTS_WEBAPP_SUSPENDED');
	}

	get eventDate() {
		return formatDate(
			new Date(this.event.content.startAt),
			this.locales,
			this.webLocale,
			this.timeZone,
			this.language
		);
	}

	get eventTime() {
		return formatTime(
			new Date(this.event.content.startAt),
			this.locales,
			this.webLocale,
			this.timeZone,
			this.language
		);
	}

	get watchNowOffer() {
		return this.event.watchNowOffer as SportOffer | undefined;
	}

	get notifyMePayload(): ToggleMySportsNotificationPayload {
		return {
			eventId: this.event.id,
			isNotificationActive: !!this.event.mysportlistEntry?.createdAt,
		};
	}

	getMonetizationType(offer: SportOffer) {
		if (offer?.package?.packageId === 695 && this.country === 'FR')
			return this.$tc('WEBAPP_FREE_TRIAL').toUpperCase();

		return offer.monetizationType;
	}

	getOfferClickoutUrl(offer: SportOffer) {
		return new ClickoutUrl(offer)
			.set('uct_country', this.country)
			.set('uct_buybox', BuyboxParam.Sport)
			.set('sid', this.domainSessionId)
			.set('uct_sport', this.event?.sport)
			.toString();
	}

	handleClickOut(offer: SportOffer | undefined, listIndex: number) {
		if (offer == null) return;

		trackClickOutSports(offer, 0, listIndex, offer.sportObjectType, 'card-v2', [this.sportContext]);
	}

	handleCardClick() {
		TrackingHelper.trackEvent(
			'userinteraction',
			{ action: 'event_clicked', property: 'card-v2', useLastRoute: true },
			[this.sportContext]
		);
	}

	handleNoOfferNotifierClick() {
		TrackingHelper.trackEvent('userinteraction', { action: 'notify_me_clicked', property: 'card-v2' }, [
			this.sportContext,
		]);
	}

	onCompetitionClick() {
		// tracking
		const sportContext = new SnowplowSportContext(this.event.sport, ObjectType.SportCompetition);
		TrackingHelper.trackEvent(
			'userinteraction',
			{ action: 'competition_clicked', label: this.context.pageType, property: 'header', useLastRoute: true },
			[sportContext]
		);
	}
}
